

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ChatMessage, Medium, ClientUser, UserLocation, Review, ClientMemory, LearnedFact, AuditLog, PromoConfig } from '../types';
import { DEFAULT_PROMO_CONFIG } from '../data/promoTemplates';
import { socket, getApiUrl } from '../services/socket';

interface ChatSession {
    id: string;
    clientId?: string;
    clientIp?: string; // Adresse IP réelle du client
    userName: string;
    userPhone?: string;   
    userEmail?: string;
    userAddress?: string;
    userLocation?: UserLocation;
    mediumName: string;
    mediumId: number;
    messages: ChatMessage[];
    isLive: boolean;
    lastActivity: Date;
    createdAt?: Date;
    hasUnreadAdmin: boolean;
    hasUnreadClient: boolean;
    isAiActive: boolean;
    couponStatus?: 'none' | 'pending' | 'valid' | 'invalid';
    couponCode?: string;
    couponPack?: '50' | '100';
    assignedAgentId?: string;
    assignedAgentName?: string;
    allowedAgentIds?: string[];
}

export interface EmailLog {
    id: string;
    date: Date;
    type: string;
    clientName: string;
    clientPhone: string;
    details: string;
    status: 'new' | 'read';
    location?: UserLocation;
}

export interface VisitLog {
    id: string;
    date: Date;
    ip: string;
    location?: UserLocation;
}

export interface EmailConfig {
    serviceId: string;
    templateId: string;
    publicKey: string;
}

export interface AdminUser {
    id: string;
    username: string;
    password?: string;
    role: 'master' | 'viewer' | 'agent';
    hasSarahAccess?: boolean;
}

export interface DailyStats {
    date: string;
    uniqueIps: number;
    interactions: number;
    locations: string[];
    visits: number;
}

export interface AdvancedStats {
    conversionRate: number;
    avgChatDuration: number;
    topLocations: { name: string, count: number, percent: number }[];
    peakHours: { hour: string, count: number, intensity: number }[];
}

type GlobalMediumStatus = 'default' | 'force_online' | 'force_offline';

interface AdminContextType {
    isAuthenticated: boolean;
    currentUser: AdminUser | null;
    login: (u: string, p: string) => Promise<boolean> | boolean;
    logout: () => void;
    
    users: AdminUser[];
    addUser: (u: AdminUser) => boolean;
    deleteUser: (id: string) => void;
    updateUser: (u: AdminUser) => void;
    updateUserPassword: (userId: string, newPassword: string) => void;

    registeredClients: ClientUser[];
    refreshClients: () => void;
    deleteClient: (clientId: string) => void;
    updateClient: (client: ClientUser) => void;
    toggleClientSuspension: (clientId: string, reason?: string) => void;

    allReviews: Review[];
    refreshReviews: () => void;
    deleteReview: (reviewId: string, mediumId: number) => void;
    markReviewRead: (reviewId: string, mediumId: number) => void;

    isAiEnabled: boolean;
    toggleAi: () => void;
    isChatAutoAnswerEnabled: boolean;
    toggleChatAutoAnswer: () => void;
    customApiKey: string;
    setCustomApiKey: (key: string) => void;
    
    emailConfig: EmailConfig;
    updateEmailConfig: (cfg: EmailConfig) => void;
    isExternalEmailEnabled: boolean; 
    toggleExternalEmail: () => void; 

    globalMediumStatus: GlobalMediumStatus;
    updateGlobalMediumStatus: (status: GlobalMediumStatus) => void;

    // --- MAINTENANCE MODE ---
    isMaintenanceMode: boolean;
    toggleMaintenanceMode: () => void;

    // --- FORMULAIRE DE BLOCAGE OBLIGATOIRE (LEAD MODAL) ---
    isLeadModalEnabled: boolean;
    toggleLeadModal: (forcedValue?: boolean) => void;

    // --- NOTIFICATIONS PROMO DYNAMIQUES ---
    promoConfig: PromoConfig;
    updatePromoConfig: (config: Partial<PromoConfig>) => Promise<void>;

    getRealDailyStats: () => DailyStats[];
    getAdvancedStats: () => AdvancedStats;
    getTodayUniqueVisitors: () => number;

    visits: VisitLog[];
    logVisit: (location: UserLocation) => void;

    sessions: ChatSession[];
    createSession: (medium: Medium, userName: string, userPhone?: string, userAddress?: string, userLocation?: UserLocation, forceId?: string, initialMessages?: ChatMessage[]) => string; 
    addMessageToSession: (sessionId: string, msg: ChatMessage) => void;
    getSession: (sessionId: string) => ChatSession | undefined;
    markAsReadByAdmin: (sessionId: string) => void;
    toggleSessionAi: (sessionId: string) => void;
    deleteSession: (sessionId: string) => void;
    deleteMessageFromSession: (sessionId: string, messageId?: string, messageIndex?: number) => void;
    updateSessionCouponStatus: (sessionId: string, status: 'none' | 'pending' | 'valid' | 'invalid', code?: string, pack?: '50' | '100') => void;
    assignAgentToSession: (sessionId: string, agentId: string | undefined, agentName: string | undefined) => void;
    toggleAgentAuthorizationForSession: (sessionId: string, agentId: string, authorized: boolean) => void;

    sessionsHistory: ChatSession[];
    deleteSessionHistory: (sessionId: string) => void;

    inbox: EmailLog[];
    logSubmission: (data: { type: string, name: string, phone: string, details: string, location?: UserLocation }) => void;
    markEmailRead: (id: string) => void;
    deleteEmail: (id: string) => void;

    // Mise à jour de la signature pour inclure mediumId
    getAiMemory: (phone: string, mediumId: number) => ClientMemory | null;
    getAllAiMemories: () => ClientMemory[];
    updateAiMemory: (
        phone: string, 
        mediumId: number, 
        name: string, 
        facts: LearnedFact[], 
        predictions: string[], 
        emotionalState: string, 
        isBlocked?: boolean,
        summary?: string,
        concerns?: string,
        problem?: string
    ) => void;
    deleteAiMemory: (key: string) => void;
    
    auditLogs: AuditLog[];
    logAuditAction: (action: string, details: string, type: 'create' | 'update' | 'delete' | 'security' | 'financial' | 'message', targetName?: string) => void;

    aiLogs: any[];
    forceRefreshData: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// --- UTILS ---
const safeJsonParse = <T,>(key: string, fallback: T): T => {
    const saved = localStorage.getItem(key);
    if (!saved || saved.trim() === "") return fallback;
    try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
        return parsed;
    } catch (e) {
        return fallback;
    }
};

const safeDate = (d: any): Date => {
    if (!d) return new Date();
    if (d instanceof Date) return d;
    if (typeof d === 'number') return new Date(d);
    let date = new Date(d);
    if (isNaN(date.getTime())) {
        if (typeof d === 'string') {
            const safariSafe = d.replace(/-/g, '/');
            date = new Date(safariSafe);
        }
    }
    return isNaN(date.getTime()) ? new Date() : date;
};

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    
    const [currentUser, setCurrentUser] = useState<AdminUser | null>(() => {
        return safeJsonParse<AdminUser | null>('admin_logged_user', null);
    });
    
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!currentUser);

    const [isLeadModalEnabled, setIsLeadModalEnabled] = useState<boolean>(() => {
        if (typeof window === 'undefined') return true;
        const saved = localStorage.getItem('astro_lead_modal_enabled');
        return saved === null ? true : saved !== 'false';
    });

    useEffect(() => {
        setIsAuthenticated(!!currentUser);
        if (currentUser) {
            socket.emit('admin_login');
        }
    }, [currentUser]);

    const DEFAULT_USERS_LIST: AdminUser[] = [
        { id: 'master-001', username: 'monta', password: 'monta1987', role: 'master', hasSarahAccess: true },
        { id: 'master-002', username: 'admin', password: 'monta1987', role: 'master', hasSarahAccess: true },
        { id: 'master-003', username: 'master', password: 'monta1987', role: 'master', hasSarahAccess: true },
        { id: 'agent-marwas', username: 'marwas', password: 'marwa2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-fabio', username: 'fabio', password: 'fabio2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-rihab', username: 'rihab', password: 'rihab2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-arbia', username: 'arbia', password: 'arbia2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-marwah', username: 'marwah', password: 'marwa2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-kawther', username: 'kawther', password: 'kawther2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-salma', username: 'salma', password: 'salma2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-chokri', username: 'chokri', password: 'chokri2026', role: 'agent', hasSarahAccess: true },
        { id: 'agent-ameni', username: 'ameni', password: 'ameni2026', role: 'agent', hasSarahAccess: true }
    ];

    const [users, setUsers] = useState<AdminUser[]>(() => {
        const data = safeJsonParse<AdminUser[]>('admin_users_list', DEFAULT_USERS_LIST);
        // Fusionner avec la liste par défaut pour garantir que tous les agents sont toujours connus
        const map = new Map<string, AdminUser>();
        if (Array.isArray(data)) {
            data.forEach(u => { if (u && u.username) map.set(u.username.toLowerCase(), u); });
        }
        DEFAULT_USERS_LIST.forEach(u => {
            if (!map.has(u.username.toLowerCase())) {
                map.set(u.username.toLowerCase(), u);
            }
        });
        return Array.from(map.values());
    });

        const fetchUsers = async () => {
        try {
            const res = await fetch(`${getApiUrl()}/api/admin/users`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.success && Array.isArray(data.users) && data.users.length > 0) {
                    setUsers(data.users);
                    localStorage.setItem('admin_users_list', JSON.stringify(data.users));
                }
            }
        } catch (e) {}
    };

    useEffect(() => {
        fetchUsers();
        fetch(`${getApiUrl()}/api/config/lead-modal`)
            .then(r => r.json())
            .then(data => {
                if (data && typeof data.isLeadModalEnabled === 'boolean') {
                    setIsLeadModalEnabled(data.isLeadModalEnabled);
                    try { localStorage.setItem('astro_lead_modal_enabled', String(data.isLeadModalEnabled)); } catch(e){}
                }
            })
            .catch(() => {});

        const handleUsersUpdated = (updatedList: AdminUser[]) => {
            if (Array.isArray(updatedList) && updatedList.length > 0) {
                setUsers(updatedList);
                localStorage.setItem('admin_users_list', JSON.stringify(updatedList));
            }
        };

        socket.on('admin_users_list', handleUsersUpdated);
        socket.on('admin_users_updated', handleUsersUpdated);
        
        const handleLeadModalSync = (data: { isLeadModalEnabled: boolean }) => {
            if (data && typeof data.isLeadModalEnabled === 'boolean') {
                setIsLeadModalEnabled(data.isLeadModalEnabled);
                try { localStorage.setItem('astro_lead_modal_enabled', String(data.isLeadModalEnabled)); } catch(e){}
            }
        };
        socket.on('sync_lead_modal_config', handleLeadModalSync);
        socket.on('sync_admin_users', handleUsersUpdated);

        return () => {
            socket.off('admin_users_list', handleUsersUpdated);
            socket.off('admin_users_updated', handleUsersUpdated);
            socket.off('sync_admin_users', handleUsersUpdated);
            socket.off('sync_lead_modal_config');
        };
    }, []);

    const [registeredClients, setRegisteredClients] = useState<ClientUser[]>([]);
    const [allReviews, setAllReviews] = useState<Review[]>([]);
    const [aiLogs, setAiLogs] = useState<any[]>([]);

    const refreshClients = () => {
        const data = safeJsonParse<ClientUser[]>('astro_clients_db', []);
        const clients = Array.isArray(data) ? data.filter(c => c && c.id) : [];
        setRegisteredClients(clients);
    };

    const refreshReviews = () => {
        const reviewsMap = safeJsonParse<Record<string, Review[]>>('astro_real_reviews', {});
        let flatReviews: Review[] = [];
        Object.keys(reviewsMap).forEach(key => {
            const mediumId = parseInt(key, 10);
            const reviews = reviewsMap[key];
            if (Array.isArray(reviews)) {
                const meds = reviews.filter(r => r && r.id).map(r => ({ ...r, mediumId }));
                flatReviews = [...flatReviews, ...meds];
            }
        });
        flatReviews.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setAllReviews(flatReviews);
    };

    // Historique et sessions de discussion persistées
    const [sessions, setSessions] = useState<ChatSession[]>(() => {
        const historyData = safeJsonParse<any[]>('admin_sessions_history', []);
        return Array.isArray(historyData) ? historyData.map(s => {
            const firstMsgTime = Array.isArray(s.messages) && s.messages[0]?.timestamp ? safeDate(s.messages[0].timestamp) : undefined;
            return {
                ...s,
                createdAt: safeDate(s.createdAt || firstMsgTime || s.lastActivity || new Date()),
                lastActivity: safeDate(s.lastActivity || new Date()),
                messages: Array.isArray(s.messages) ? s.messages.map((m: any) => ({...m, timestamp: safeDate(m.timestamp)})) : []
            };
        }) : [];
    });
    const [inbox, setInbox] = useState<EmailLog[]>([]);

    useEffect(() => {
        // Socket Listeners
        socket.on('sync_all_sessions', (data: any[]) => {
            const cleanSessions = data.map(s => ({
                ...s,
                lastActivity: safeDate(s.lastActivity),
                messages: s.messages.map((m: any) => ({...m, timestamp: safeDate(m.timestamp)}))
            }));
            setSessions(cleanSessions);
        });

        socket.on('sync_inbox', (data: any[]) => {
            const cleanInbox = data.map(i => ({...i, date: safeDate(i.date)}));
            setInbox(cleanInbox);
        });

        socket.on('session_updated', (updatedSession: any) => {
            if (!updatedSession) return;
            
            // SÉCURITÉ STRICTE : Si non-admin, refuser d'enregistrer les sessions d'autres clients
            const currentClient = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('astro_current_client') || 'null') : null;
            const isAdmin = Boolean(currentUser);
            if (!isAdmin) {
                const myId = currentClient?.id;
                const isMySession = myId && (updatedSession.clientId === myId || updatedSession.id?.includes(myId));
                if (!isMySession) return;
            }

            setSessions(prev => {
                const exists = prev.find(s => s.id === updatedSession.id);
                const cleanSession = {
                    ...updatedSession,
                    lastActivity: safeDate(updatedSession.lastActivity),
                    messages: (updatedSession.messages || []).map((m: any) => ({...m, timestamp: safeDate(m.timestamp)}))
                };
                
                if (exists) {
                    return prev.map(s => s.id === updatedSession.id ? cleanSession : s);
                } else {
                    return [cleanSession, ...prev];
                }
            });
        });

        socket.on('receive_message', (data: any) => {
            const sessionId = data?.sessionId;
            const message = data?.message || data;
            
            const currentClient = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('astro_current_client') || 'null') : null;
            const isAdmin = Boolean(currentUser);
            if (!isAdmin) {
                const myId = currentClient?.id;
                const isMySession = myId && sessionId && sessionId.includes(myId);
                if (!isMySession) return;
            }

            if (sessionId && message) {
                setSessions(prev => prev.map(s => {
                    if (s.id === sessionId) {
                        if (s.messages.some(m => m.id === message.id)) return s;
                        return {
                            ...s,
                            messages: [...s.messages, { ...message, timestamp: safeDate(message.timestamp) }],
                            lastActivity: safeDate(message.timestamp || new Date())
                        };
                    }
                    return s;
                }));
            }
        });

        socket.on('session_deleted', (sessionId: string) => {
            setSessions(prev => prev.filter(s => s.id !== sessionId));
            setSessionsHistory(prev => {
                const next = prev.filter(s => s.id !== sessionId);
                try { localStorage.setItem('admin_sessions_history', JSON.stringify(next)); } catch(e){}
                return next;
            });
        });

        socket.on('message_deleted', ({ sessionId, messageId, messageIndex }: any) => {
            setSessions(prev => prev.map(s => {
                if (s.id === sessionId) {
                    let updated = [...s.messages];
                    if (messageId) {
                        updated = updated.filter(m => m.id !== messageId);
                    } else if (typeof messageIndex === 'number' && messageIndex >= 0 && messageIndex < updated.length) {
                        updated.splice(messageIndex, 1);
                    }
                    return { ...s, messages: updated };
                }
                return s;
            }));
        });

        socket.on('new_inbox_item', (item: any) => {
            const cleanItem = {...item, date: safeDate(item.date)};
            setInbox(prev => [cleanItem, ...prev]);
        });

        socket.on('medium_status_changed', (status: GlobalMediumStatus) => {
            if (status) {
                setGlobalMediumStatus(status);
                try { localStorage.setItem('admin_medium_global_status', status); } catch (e) {}
            }
        });

        socket.on('ai_status_changed', (data: { isAiEnabled?: boolean; isChatAutoAnswerEnabled?: boolean }) => {
            if (data) {
                if (typeof data.isAiEnabled === 'boolean') {
                    setIsAiEnabled(data.isAiEnabled);
                    try { localStorage.setItem('admin_ai_enabled', String(data.isAiEnabled)); } catch (e) {}
                }
                if (typeof data.isChatAutoAnswerEnabled === 'boolean') {
                    setIsChatAutoAnswerEnabled(data.isChatAutoAnswerEnabled);
                    try { localStorage.setItem('admin_chat_auto_answer_enabled', String(data.isChatAutoAnswerEnabled)); } catch (e) {}
                }
            }
        });

        socket.on('sync_clients', (clients: ClientUser[]) => {
            if (Array.isArray(clients)) {
                const cleanClients = clients.filter(c => c && c.id);
                setRegisteredClients(cleanClients);
                try { localStorage.setItem('astro_clients_db', JSON.stringify(cleanClients)); } catch (e) {}
            }
        });

        socket.on('sync_reviews', (reviews: Review[]) => {
            if (Array.isArray(reviews)) {
                setAllReviews(reviews);
            }
        });

        socket.on('sync_ai_memories', (memories: any) => {
            if (memories) {
                if (Array.isArray(memories)) {
                    const record: Record<string, any> = {};
                    memories.forEach((m: any) => { if (m && m.key) record[m.key] = m; });
                    setAiMemories(record);
                } else if (typeof memories === 'object') {
                    setAiMemories(memories);
                }
            }
        });

        socket.on('sync_visits', (v: VisitLog[]) => {
            if (Array.isArray(v)) {
                const cleanVisits = v.filter(val => val && val.date).map(val => ({ ...val, date: safeDate(val.date) }));
                setVisits(cleanVisits);
            }
        });

        socket.on('sync_ai_logs', (logs: any[]) => {
            if (Array.isArray(logs)) setAiLogs(logs);
        });

        socket.on('ai_log_entry', (entry: any) => {
            if (entry && entry.id) {
                setAiLogs(prev => [entry, ...prev.filter(x => x.id !== entry.id)].slice(0, 100));
            }
        });

        socket.on('sync_promo_config', (cfg: any) => {
            if (cfg && typeof cfg === 'object') {
                const merged = { ...DEFAULT_PROMO_CONFIG, ...cfg };
                setPromoConfig(merged);
                try { localStorage.setItem('astro_promo_config', JSON.stringify(merged)); } catch(e){}
            }
        });

        socket.on('promo_config_updated', (cfg: any) => {
            if (cfg && typeof cfg === 'object') {
                const merged = { ...DEFAULT_PROMO_CONFIG, ...cfg };
                setPromoConfig(merged);
                try { localStorage.setItem('astro_promo_config', JSON.stringify(merged)); } catch(e){}
            }
        });

        return () => {
            socket.off('sync_all_sessions');
            socket.off('sync_inbox');
            socket.off('session_updated');
            socket.off('receive_message');
            socket.off('session_deleted');
            socket.off('new_inbox_item');
            socket.off('medium_status_changed');
            socket.off('ai_status_changed');
            socket.off('sync_clients');
            socket.off('sync_reviews');
            socket.off('sync_ai_memories');
            socket.off('sync_visits');
            socket.off('sync_ai_logs');
            socket.off('ai_log_entry');
            socket.off('sync_promo_config');
            socket.off('promo_config_updated');
        };
    }, []);

    // Configuration des notifications promotionnelles
    const [promoConfig, setPromoConfig] = useState<PromoConfig>(() => {
        const saved = safeJsonParse<PromoConfig | null>('astro_promo_config', null);
        return saved ? { ...DEFAULT_PROMO_CONFIG, ...saved } : DEFAULT_PROMO_CONFIG;
    });

    const updatePromoConfig = async (newConfig: Partial<PromoConfig>) => {
        const updated = { ...promoConfig, ...newConfig };
        setPromoConfig(updated);
        try { localStorage.setItem('astro_promo_config', JSON.stringify(updated)); } catch(e){}
        
        socket.emit('update_promo_config', updated);
        try {
            await fetch('/api/promo/config', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updated)
            });
        } catch (e) {
            console.warn('[AdminContext] Erreur sauvegarde config promo:', e);
        }
    };

    // Configuration locale persistée (Auto-Chat désactivé par défaut)
    const [isAiEnabled, setIsAiEnabled] = useState(() => localStorage.getItem('admin_ai_enabled') !== 'false');
    const [isChatAutoAnswerEnabled, setIsChatAutoAnswerEnabled] = useState(() => localStorage.getItem('admin_chat_auto_answer_enabled') === 'true');
    const [customApiKey, setCustomApiKeyState] = useState(() => localStorage.getItem('admin_api_key') || '');
    const [emailConfig, setEmailConfig] = useState<EmailConfig>(() => safeJsonParse<EmailConfig>('admin_email_config', { serviceId: 'service_ctcp5yu', templateId: 'template_qcacz6n', publicKey: 'SjvLBrF8kVkSCXD35' }));
    const [isExternalEmailEnabled, setIsExternalEmailEnabled] = useState(() => localStorage.getItem('admin_external_email_enabled') !== 'false');
    const [globalMediumStatus, setGlobalMediumStatus] = useState<GlobalMediumStatus>(() => (localStorage.getItem('admin_medium_global_status') as GlobalMediumStatus) || 'default');
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(() => localStorage.getItem('admin_maintenance_mode') === 'true');
    
    const [visits, setVisits] = useState<VisitLog[]>(() => {
        const data = safeJsonParse<any[]>('admin_visits_log', []);
        return Array.isArray(data) ? data.filter(v => v && v.date).map((v: any) => ({...v, date: safeDate(v.date)})) : [];
    });

    // AI Memory State - Clé composite "PHONE_MEDIUMID"
    const [aiMemories, setAiMemories] = useState<Record<string, ClientMemory>>(() => safeJsonParse('astro_ai_memories', {}));

    // Audit Logs State
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>(() => {
        return safeJsonParse<AuditLog[]>('admin_audit_logs', []);
    });

    useEffect(() => {
        localStorage.setItem('admin_audit_logs', JSON.stringify(auditLogs));
    }, [auditLogs]);

    // WebSocket Listeners for real-time notifications and audit logs
    useEffect(() => {
        const playNotificationSound = () => {
            try {
                const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(587.33, ctx.currentTime);
                osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
                gain.gain.setValueAtTime(0.08, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start();
                osc.stop(ctx.currentTime + 0.35);
            } catch (e) {
                // AudioContext policy
            }
        };

        socket.on('admin_notification', (data: any) => {
            playNotificationSound();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('astro_admin_notification', { detail: data }));
            }
        });

        socket.on('new_audit_log', (log: AuditLog) => {
            setAuditLogs(prev => [log, ...prev]);
        });

        return () => {
            socket.off('admin_notification');
            socket.off('new_audit_log');
        };
    }, []);

    const logAuditAction = (action: string, details: string, type: 'create' | 'update' | 'delete' | 'security' | 'financial' | 'message', targetName?: string) => {
        const adminName = currentUser?.username || 'Administration';
        const newLog: AuditLog = {
            id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            action,
            adminName,
            targetName,
            details,
            timestamp: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            type
        };
        setAuditLogs(prev => [newLog, ...prev]);
        socket.emit('audit_action', newLog);
    };

    // Historique des sessions de discussion
    const [sessionsHistory, setSessionsHistory] = useState<ChatSession[]>(() => {
        const data = safeJsonParse<any[]>('admin_sessions_history', []);
        return Array.isArray(data) ? data.map(s => {
            const firstMsgTime = Array.isArray(s.messages) && s.messages[0]?.timestamp ? safeDate(s.messages[0].timestamp) : undefined;
            return {
                ...s,
                createdAt: safeDate(s.createdAt || firstMsgTime || s.lastActivity || new Date()),
                lastActivity: safeDate(s.lastActivity || new Date()),
                messages: Array.isArray(s.messages) ? s.messages.map((m: any) => ({...m, timestamp: safeDate(m.timestamp)})) : []
            };
        }) : [];
    });

    useEffect(() => { localStorage.setItem('admin_visits_log', JSON.stringify(visits)); }, [visits]);
    useEffect(() => { localStorage.setItem('admin_users_list', JSON.stringify(users)); }, [users]);
    useEffect(() => { localStorage.setItem('admin_ai_enabled', String(isAiEnabled)); }, [isAiEnabled]);
    useEffect(() => { localStorage.setItem('admin_chat_auto_answer_enabled', String(isChatAutoAnswerEnabled)); }, [isChatAutoAnswerEnabled]);
    useEffect(() => { localStorage.setItem('admin_api_key', customApiKey); }, [customApiKey]);
    useEffect(() => { localStorage.setItem('admin_medium_global_status', globalMediumStatus); }, [globalMediumStatus]);
    useEffect(() => { localStorage.setItem('admin_maintenance_mode', String(isMaintenanceMode)); }, [isMaintenanceMode]);
    useEffect(() => { localStorage.setItem('astro_ai_memories', JSON.stringify(aiMemories)); }, [aiMemories]);
    useEffect(() => { 
        localStorage.setItem('admin_sessions_history', JSON.stringify(sessions)); 
        setSessionsHistory(sessions);
    }, [sessions]);

    const login = async (u: string, p: string): Promise<boolean> => {
        const cleanU = (u || '').trim();
        const cleanP = (p || '').trim();

        if (!cleanU || !cleanP) return false;

        try {
            const res = await fetch(`${getApiUrl()}/api/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: cleanU, password: cleanP }),
                credentials: 'include'
            });

            if (res.ok) {
                const data = await res.json();
                if (data && data.success && data.user) {
                    const adminUser: AdminUser = {
                        id: data.user.id,
                        username: data.user.username,
                        password: cleanP,
                        role: data.user.role || 'agent',
                        hasSarahAccess: Boolean(data.user.hasSarahAccess),
                        createdAt: new Date().toISOString()
                    };
                    setCurrentUser(adminUser);
                    localStorage.setItem('admin_logged_user', JSON.stringify(adminUser));
                    socket.emit('admin_login');
                    fetchUsers();

                    // Si c'est un agent, configurer l'URL /agent
                    if (adminUser.role === 'agent' && typeof window !== 'undefined') {
                        if (!window.location.pathname.startsWith('/agent')) {
                            window.history.replaceState({}, '', '/agent');
                        }
                    }
                    return true;
                }
            }
        } catch (e) {
            console.warn('[AdminContext] Erreur réseau connexion API serveur, tentative de secours locale...', e);
        }

        // Secours d'authentification locale (si serveur en démarrage ou réseau instable)
        const foundUser = users.find(user => 
            (user.username || '').trim().toLowerCase() === cleanU.toLowerCase() && 
            (user.password || '').trim() === cleanP
        );
        if (foundUser) {
            const adminUser: AdminUser = {
                ...foundUser,
                password: cleanP
            };
            setCurrentUser(adminUser);
            localStorage.setItem('admin_logged_user', JSON.stringify(adminUser));
            socket.emit('admin_login'); 
            if (adminUser.role === 'agent' && typeof window !== 'undefined') {
                if (!window.location.pathname.startsWith('/agent')) {
                    window.history.replaceState({}, '', '/agent');
                }
            }
            return true;
        }
        return false;
    };

    const logout = async () => {
        try {
            await fetch(`${getApiUrl()}/api/admin/logout`, { method: 'POST', credentials: 'include' });
        } catch (e) {}
        setCurrentUser(null);
        localStorage.removeItem('admin_logged_user');
    };

    // --- ACTIONS ---

    const createSession = (
        medium: Medium, 
        userName: string, 
        userPhone?: string, 
        userAddress?: string, 
        userLocation?: UserLocation, 
        forceId?: string, 
        initialMessages?: ChatMessage[],
        clientId?: string,
        userEmail?: string
    ) => {
        // SÉCURITÉ : Empêcher un visiteur non authentifié d'usurper un ID client enregistré
        const currentClient = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('astro_current_client') || 'null') : null;
        const isAdmin = Boolean(currentUser);
        
        let safeClientId = clientId;
        if (!isAdmin && clientId && clientId.startsWith('client-') && (!currentClient || currentClient.id !== clientId)) {
            safeClientId = currentClient ? currentClient.id : undefined;
        }

        const clientKey = safeClientId || (userPhone ? userPhone.replace(/\s+/g, '') : (userName || 'guest').trim().toLowerCase().replace(/\s+/g, '_'));
        const defaultIsolatedId = medium.id === 9999 ? `sarah_${clientKey}` : `session_${clientKey}_m${medium.id}`;
        const sessionId = (!isAdmin && !currentClient && forceId && forceId.includes('client-')) ? defaultIsolatedId : (forceId || defaultIsolatedId);
        
        // RECHERCHE D'UNE SESSION EXISTANTE STRICTEMENT VALIDÉE
        const existingSession = sessions.find(s => {
            if (s.id !== sessionId) return false;
            if (!isAdmin && !currentClient && s.clientId && s.clientId.startsWith('client-')) return false;
            return true;
        }) || sessionsHistory.find(s => {
            if (s.id !== sessionId) return false;
            if (!isAdmin && !currentClient && s.clientId && s.clientId.startsWith('client-')) return false;
            return true;
        });

        const targetSessionId = existingSession ? existingSession.id : sessionId;
        
        const mergedMessages = (existingSession && Array.isArray(existingSession.messages) && existingSession.messages.length > 0)
            ? existingSession.messages
            : (initialMessages || []);

        const newSession: ChatSession = {
            id: targetSessionId,
            clientId: clientId || existingSession?.clientId || clientKey,
            userName: userName || existingSession?.userName,
            userPhone: userPhone || existingSession?.userPhone,
            userEmail: userEmail || existingSession?.userEmail,
            userAddress: userAddress || existingSession?.userAddress,
            userLocation: userLocation || existingSession?.userLocation,
            mediumName: medium.name,
            mediumId: medium.id,
            messages: mergedMessages,
            isLive: true,
            lastActivity: new Date(),
            hasUnreadAdmin: existingSession ? existingSession.hasUnreadAdmin : true,
            hasUnreadClient: false,
            isAiActive: existingSession ? existingSession.isAiActive : false,
            assignedAgentId: existingSession ? existingSession.assignedAgentId : undefined,
            assignedAgentName: existingSession ? existingSession.assignedAgentName : undefined,
            allowedAgentIds: existingSession ? existingSession.allowedAgentIds : []
        };
        
        socket.emit('start_session', newSession);
        
        setSessions(prev => {
            const existsIndex = prev.findIndex(s => s.id === targetSessionId || (
                (String(s.mediumId) === String(medium.id) || (medium.id === 9999 && s.id.startsWith('sarah_'))) && (
                    (clientId && s.clientId === clientId) ||
                    (userPhone && s.userPhone && s.userPhone.replace(/\s+/g, '') === userPhone.replace(/\s+/g, ''))
                )
            ));

            if (existsIndex >= 0) {
                const next = [...prev];
                next[existsIndex] = { ...next[existsIndex], ...newSession };
                return next;
            }
            return [newSession, ...prev];
        });
        
        return targetSessionId;
    };

    const addMessageToSession = (sessionId: string, msg: ChatMessage) => {
        socket.emit('send_message', { sessionId, message: msg, mediumId: msg.mediumId, mediumName: msg.mediumName });
        setSessions(prev => prev.map(s => {
            if (s.id === sessionId) {
                let updatedSession = {
                    ...s,
                    messages: [...s.messages, msg],
                    lastActivity: new Date()
                };

                // Si c'est un message envoyé par un modèle (réponse humaine d'un agent)
                if (msg.role === 'model' && currentUser) {
                    // S'il n'y a pas encore d'agent assigné et que l'utilisateur actuel est un agent
                    if (!s.assignedAgentId && currentUser.role === 'agent') {
                        updatedSession.assignedAgentId = currentUser.id;
                        updatedSession.assignedAgentName = currentUser.username;
                        updatedSession.allowedAgentIds = [currentUser.id];
                    }
                }

                return updatedSession;
            }
            return s;
        }));
    };

    const assignAgentToSession = (sessionId: string, agentId: string | undefined, agentName: string | undefined) => {
        setSessions(prev => prev.map(s => {
            if (s.id === sessionId) {
                const currentAllowed = s.allowedAgentIds || [];
                const updatedAllowed = agentId 
                    ? Array.from(new Set([...currentAllowed, agentId]))
                    : currentAllowed;
                const updated = {
                    ...s,
                    assignedAgentId: agentId,
                    assignedAgentName: agentName,
                    allowedAgentIds: updatedAllowed,
                    lastActivity: new Date()
                };
                socket.emit('session_updated', updated);
                return updated;
            }
            return s;
        }));
    };

    const toggleAgentAuthorizationForSession = (sessionId: string, agentId: string, authorized: boolean) => {
        setSessions(prev => prev.map(s => {
            if (s.id === sessionId) {
                const currentAllowed = s.allowedAgentIds || [];
                const updatedAllowed = authorized 
                    ? Array.from(new Set([...currentAllowed, agentId]))
                    : currentAllowed.filter(id => id !== agentId);
                
                const updated = {
                    ...s,
                    allowedAgentIds: updatedAllowed,
                    lastActivity: new Date()
                };
                socket.emit('session_updated', updated);
                return updated;
            }
            return s;
        }));
    };

    const deleteSession = (sessionId: string) => {
        socket.emit('delete_session', sessionId);
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        setSessionsHistory(prev => {
            const next = prev.filter(s => s.id !== sessionId);
            try { localStorage.setItem('admin_sessions_history', JSON.stringify(next)); } catch(e){}
            return next;
        });
    };

    const deleteMessageFromSession = (sessionId: string, messageId?: string, messageIndex?: number) => {
        socket.emit('delete_message', { sessionId, messageId, messageIndex });
        setSessions(prev => prev.map(s => {
            if (s.id === sessionId) {
                let updated = [...s.messages];
                if (messageId) {
                    updated = updated.filter(m => m.id !== messageId);
                } else if (typeof messageIndex === 'number' && messageIndex >= 0 && messageIndex < updated.length) {
                    updated.splice(messageIndex, 1);
                }
                return { ...s, messages: updated };
            }
            return s;
        }));
    };

    const updateSessionCouponStatus = (
        sessionId: string, 
        status: 'none' | 'pending' | 'valid' | 'invalid', 
        code?: string, 
        pack?: '50' | '100'
    ) => {
        let updatedTarget: any = null;
        setSessions(prev => {
            const exists = prev.some(s => s.id === sessionId);
            if (exists) {
                return prev.map(s => {
                    if (s.id === sessionId) {
                        updatedTarget = {
                            ...s,
                            couponStatus: status,
                            couponCode: code !== undefined ? code : s.couponCode,
                            couponPack: pack !== undefined ? pack : s.couponPack,
                            lastActivity: new Date()
                        };
                        return updatedTarget;
                    }
                    return s;
                });
            } else {
                updatedTarget = {
                    id: sessionId,
                    mediumId: 9999,
                    userName: 'Client Transcash',
                    messages: [],
                    lastActivity: new Date(),
                    couponStatus: status,
                    couponCode: code || '',
                    couponPack: pack || '50'
                };
                return [updatedTarget, ...prev];
            }
        });
        if (updatedTarget) {
            socket.emit('session_updated', updatedTarget);
        }
    };

    const logSubmission = (data: { type: string, name: string, phone: string, details: string, location?: UserLocation }) => {
        const newLog: EmailLog = {
            id: Date.now().toString() + Math.random(),
            date: new Date(),
            type: data.type,
            clientName: data.name,
            clientPhone: data.phone,
            details: data.details,
            status: 'new',
            location: data.location
        };
        socket.emit('new_submission', newLog);
        setInbox(prev => [newLog, ...prev]);
    };

    const markAsReadByAdmin = (sessionId: string) => {
        socket.emit('admin_read_session', sessionId);
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, hasUnreadAdmin: false } : s));
    };

    const toggleSessionAi = (sessionId: string) => setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, isAiActive: !s.isAiActive } : s));

    const logVisit = useCallback((location: UserLocation) => {
        setVisits(prev => {
            const now = new Date();
            const recentVisit = prev.find(v => v.ip === location.ip && (now.getTime() - safeDate(v.date).getTime()) < 60 * 60 * 1000);
            if (recentVisit) return prev;
            const newVisit = { id: `visit-${Date.now()}`, date: now, ip: location.ip, location };
            socket.emit('log_visit', newVisit);
            return [newVisit, ...prev].slice(0, 1000);
        });
    }, []);

    // Getters
    const getSession = (sessionId: string) => sessions.find(s => s.id === sessionId);
    
    // Stats (Reads from local visits for now)
    const getRealDailyStats = (): DailyStats[] => {
        const statsMap = new Map<string, any>();
        visits.forEach(v => {
            const dateKey = safeDate(v.date).toLocaleDateString('fr-FR');
            if (!statsMap.has(dateKey)) statsMap.set(dateKey, { ips: new Set(), interactions: 0, locs: new Set(), visits: 0 });
            const entry = statsMap.get(dateKey);
            entry.visits += 1;
            entry.ips.add(v.ip);
            if (v.location?.city) entry.locs.add(v.location.city);
        });

        // Add sessions to stats
        sessions.forEach(s => {
            const dateKey = safeDate(s.lastActivity).toLocaleDateString('fr-FR');
            if (!statsMap.has(dateKey)) statsMap.set(dateKey, { ips: new Set(), interactions: 0, locs: new Set(), visits: 0 });
            const entry = statsMap.get(dateKey);
            entry.interactions += 1;
        });

        const result: DailyStats[] = [];
        statsMap.forEach((val, key) => {
            result.push({
                date: key,
                uniqueIps: val.ips.size,
                interactions: val.interactions,
                locations: Array.from(val.locs),
                visits: val.visits
            });
        });
        return result.sort((a, b) => new Date(b.date.split('/').reverse().join('-')).getTime() - new Date(a.date.split('/').reverse().join('-')).getTime());
    };

    const getAdvancedStats = (): AdvancedStats => {
        const totalVisits = visits.length;
        const totalInteractions = sessions.length;
        const conversionRate = totalVisits > 0 ? (totalInteractions / totalVisits) * 100 : 0;
        
        // Duration
        let totalDuration = 0;
        let countedSessions = 0;
        sessions.forEach(s => {
            if (s.messages.length > 1) {
                const start = safeDate(s.messages[0].timestamp).getTime();
                const end = safeDate(s.messages[s.messages.length - 1].timestamp).getTime();
                totalDuration += (end - start);
                countedSessions++;
            }
        });
        const avgDuration = countedSessions > 0 ? (totalDuration / countedSessions) / 60000 : 0;

        // Locations
        const locMap: Record<string, number> = {};
        visits.forEach(v => {
            if (v.location?.city) locMap[v.location.city] = (locMap[v.location.city] || 0) + 1;
        });
        const topLocations = Object.entries(locMap)
            .map(([name, count]) => ({ name, count, percent: (count / totalVisits) * 100 }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

        return {
            conversionRate,
            avgChatDuration: avgDuration,
            topLocations,
            peakHours: [] 
        };
    };

    const getTodayUniqueVisitors = () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const todayVisits = visits.filter(v => safeDate(v.date).toLocaleDateString('fr-FR') === today);
        return new Set(todayVisits.map(v => v.ip)).size;
    };

    const forceRefreshData = () => {
        socket.emit('admin_login');
        refreshClients();
        refreshReviews();
    };

    // Review & Client Mgmt
    const deleteReview = (reviewId: string, mediumId: number) => { 
        const reviewsMap = safeJsonParse<Record<string, Review[]>>('astro_real_reviews', {});
        const list = reviewsMap[mediumId] || [];
        reviewsMap[mediumId] = list.filter(r => r.id !== reviewId);
        localStorage.setItem('astro_real_reviews', JSON.stringify(reviewsMap));
        refreshReviews();
    };
    
    const markReviewRead = (reviewId: string, mediumId: number) => { /* ... */ };
    
    const deleteClient = (clientId: string) => {
        const data = safeJsonParse<ClientUser[]>('astro_clients_db', []);
        const clientToDelete = data.find(c => c.id === clientId);
        
        // 1. Supprimer de la base clients
        const newData = data.filter(c => c.id !== clientId);
        localStorage.setItem('astro_clients_db', JSON.stringify(newData));
        socket.emit('delete_client', clientId);

        if (clientToDelete) {
            const clientPhone = clientToDelete.phone ? clientToDelete.phone.trim() : '';
            const clientName = clientToDelete.name ? clientToDelete.name.trim().toLowerCase() : '';

            // 2. Déconnecter et purger la session client si actuellement connecté
            const currentClient = safeJsonParse<ClientUser | null>('astro_current_client', null);
            if (currentClient && (currentClient.id === clientId || (clientPhone && currentClient.phone === clientPhone))) {
                localStorage.removeItem('astro_current_client');
                window.dispatchEvent(new CustomEvent('astro_client_purged', { detail: { clientId } }));
            }

            // 3. Purger les mémoires du Cerveau IA pour ce client
            setAiMemories(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(key => {
                    const mem = next[key];
                    if ((clientPhone && mem.phone === clientPhone) || (clientName && mem.name?.trim().toLowerCase() === clientName)) {
                        delete next[key];
                    }
                });
                localStorage.setItem('astro_ai_memories', JSON.stringify(next));
                return next;
            });

            // 4. Supprimer les sessions de chat en direct actives pour ce client
            setSessions(prev => {
                const filtered = prev.filter(s => {
                    const isMatch = (clientPhone && s.userPhone === clientPhone) || (clientName && s.userName?.trim().toLowerCase() === clientName);
                    if (isMatch) {
                        socket.emit('delete_session', s.id);
                    }
                    return !isMatch;
                });
                return filtered;
            });

            // 5. Supprimer l'historique complet des sessions de chat de ce client
            setSessionsHistory(prev => {
                const filtered = prev.filter(s => {
                    const isMatch = (clientPhone && s.userPhone === clientPhone) || (clientName && s.userName?.trim().toLowerCase() === clientName);
                    return !isMatch;
                });
                localStorage.setItem('astro_sessions_history', JSON.stringify(filtered));
                return filtered;
            });

            // 6. Supprimer les messages de contact / formulaires de ce client
            setInbox(prev => {
                const filtered = prev.filter(e => {
                    const isMatch = (clientPhone && e.clientPhone === clientPhone) || (clientName && e.clientName?.trim().toLowerCase() === clientName);
                    return !isMatch;
                });
                return filtered;
            });

            // 7. Supprimer les avis déposés par ce client
            const reviewsMap = safeJsonParse<Record<string, Review[]>>('astro_real_reviews', {});
            let changedReviews = false;
            Object.keys(reviewsMap).forEach(k => {
                if (Array.isArray(reviewsMap[k])) {
                    const initialLen = reviewsMap[k].length;
                    reviewsMap[k] = reviewsMap[k].filter(r => r.author?.trim().toLowerCase() !== clientName);
                    if (reviewsMap[k].length !== initialLen) changedReviews = true;
                }
            });
            if (changedReviews) {
                localStorage.setItem('astro_real_reviews', JSON.stringify(reviewsMap));
                refreshReviews();
            }
        }

        refreshClients();
    };

    const toggleClientSuspension = (clientId: string, reason?: string) => {
        const data = safeJsonParse<ClientUser[]>('astro_clients_db', []);
        const client = data.find(c => c.id === clientId);
        if (!client) return;

        const willSuspend = !client.isSuspended;
        const updatedClient: ClientUser = {
            ...client,
            isSuspended: willSuspend,
            suspensionReason: willSuspend ? (reason || "Non-respect des conditions d'utilisation") : undefined,
            suspendedAt: willSuspend ? new Date().toISOString() : undefined
        };

        const newData = data.map(c => c.id === clientId ? updatedClient : c);
        localStorage.setItem('astro_clients_db', JSON.stringify(newData));
        socket.emit('client_updated', updatedClient);

        // Si le client est connecté sur cette instance, notifier
        const currentClient = safeJsonParse<ClientUser | null>('astro_current_client', null);
        if (currentClient && currentClient.id === clientId) {
            if (willSuspend) {
                localStorage.setItem('astro_current_client', JSON.stringify(updatedClient));
                window.dispatchEvent(new CustomEvent('astro_client_suspended', { detail: { reason: updatedClient.suspensionReason } }));
            } else {
                localStorage.setItem('astro_current_client', JSON.stringify(updatedClient));
                window.dispatchEvent(new CustomEvent('astro_client_unsuspended', { detail: { client: updatedClient } }));
            }
        }

        refreshClients();
    };

    const updateClient = (client: ClientUser) => {
        const data = safeJsonParse<ClientUser[]>('astro_clients_db', []);
        const newData = data.map(c => c.id === client.id ? client : c);
        localStorage.setItem('astro_clients_db', JSON.stringify(newData));
        socket.emit('client_updated', client);
        refreshClients();
    };
    
    // AI Memory Implementation - ISOLATION PAR MEDIUM
    const getAiMemory = (phone: string, mediumId: number) => {
        if (!phone) return null;
        const key = `${phone}_${mediumId}`;
        return aiMemories[key] || null;
    };

    const getAllAiMemories = () => Object.values(aiMemories);

    const updateAiMemory = (
        phone: string, 
        mediumId: number, 
        name: string, 
        facts: LearnedFact[], 
        predictions: string[], 
        emotionalState: string, 
        isBlocked?: boolean,
        summary?: string,
        concerns?: string,
        problem?: string
    ) => {
        const key = `${phone}_${mediumId}`;
        const current = aiMemories[key] || {
            phone, 
            mediumId, 
            name, 
            firstSeen: new Date().toISOString(), 
            lastSeen: new Date().toISOString(),
            facts: [], 
            predictions: [], 
            interactionCount: 0, 
            emotionalProfile: "Neutre",
            isBlocked: false,
            summary: "",
            concerns: "",
            problem: ""
        };

        const safeIncomingFacts: LearnedFact[] = (facts || []).map(f => {
            if (!f) return { category: 'psychologie', fact: '' };
            if (typeof f === 'string') return { category: 'psychologie', fact: f };
            return {
                category: f.category || 'psychologie',
                fact: typeof f.fact === 'string' ? f.fact : (typeof f.fact === 'object' && f.fact !== null ? ((f.fact as any).fact || JSON.stringify(f.fact)) : String(f.fact || '')),
                date: typeof f.date === 'string' ? f.date : undefined
            };
        }).filter(f => f.fact && f.fact.trim().length > 0);

        const safeIncomingPreds: string[] = (predictions || []).map(p => {
            if (!p) return '';
            if (typeof p === 'string') return p.trim();
            if (typeof p === 'object' && p !== null) return ((p as any).text || (p as any).fact || (p as any).prediction || JSON.stringify(p)).trim();
            return String(p).trim();
        }).filter(Boolean);

        const updated: ClientMemory = {
            ...current,
            lastSeen: new Date().toISOString(),
            facts: [...(current.facts || []), ...safeIncomingFacts], 
            predictions: [...(current.predictions || []), ...safeIncomingPreds],
            emotionalProfile: typeof emotionalState === 'string' ? emotionalState : (current.emotionalProfile || "Neutre"),
            interactionCount: (current.interactionCount || 0) + 1,
            isBlocked: isBlocked !== undefined ? isBlocked : current.isBlocked,
            summary: typeof summary === 'string' ? summary : (current.summary || ""),
            concerns: typeof concerns === 'string' ? concerns : (current.concerns || ""),
            problem: typeof problem === 'string' ? problem : (current.problem || "")
        };

        setAiMemories(prev => ({ ...prev, [key]: updated }));
        socket.emit('update_ai_memory', { ...updated, key });
    };

    const deleteAiMemory = (key: string) => {
        const newMems = { ...aiMemories };
        delete newMems[key];
        setAiMemories(newMems);
        socket.emit('delete_ai_memory', key);
    };

    const deleteSessionHistory = (sessionId: string) => {
        setSessionsHistory(prev => prev.filter(s => s.id !== sessionId));
    };

    // Other Setters
    const addUser = (u: AdminUser) => {
        const cleanUser = (u.username || '').trim();
        const cleanPass = (u.password || '').trim();

        const userObj: AdminUser = {
            id: u.id || `admin-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            username: cleanUser,
            password: cleanPass,
            role: u.role,
            hasSarahAccess: u.hasSarahAccess
        };

        // 1. Mise à jour immédiate de l'état local & localStorage
        setUsers(prev => {
            const filtered = prev.filter(existing => (existing.username || '').toLowerCase() !== cleanUser.toLowerCase());
            const updatedList = [...filtered, userObj];
            try { localStorage.setItem('admin_users_list', JSON.stringify(updatedList)); } catch (e) {}
            return updatedList;
        });

        // 2. Émission Socket.IO instantanée pour tous les postes
        socket.emit('save_admin_user', userObj);

        // 3. Appel API REST pour persistance serveur
        fetch(`${getApiUrl()}/api/admin/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userObj)
        }).then(res => res.json()).then(data => {
            if (data && data.success && Array.isArray(data.users)) {
                setUsers(data.users);
                try { localStorage.setItem('admin_users_list', JSON.stringify(data.users)); } catch (e) {}
            }
        }).catch(err => console.error('[AdminContext] Erreur création utilisateur serveur:', err));

        return true;
    };

    const deleteUser = (id: string) => {
        setUsers(prev => {
            const updated = prev.filter(u => u.id !== id);
            try { localStorage.setItem('admin_users_list', JSON.stringify(updated)); } catch (e) {}
            return updated;
        });

        socket.emit('delete_admin_user', id);

        fetch(`${getApiUrl()}/api/admin/users/${id}`, {
            method: 'DELETE'
        }).then(res => res.json()).then(data => {
            if (data && data.success && Array.isArray(data.users)) {
                setUsers(data.users);
                try { localStorage.setItem('admin_users_list', JSON.stringify(data.users)); } catch (e) {}
            }
        }).catch(err => console.error('[AdminContext] Erreur suppression utilisateur serveur:', err));
    };

    const updateUser = (updated: AdminUser) => {
        setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
        if (currentUser && currentUser.id === updated.id) {
            setCurrentUser(updated);
            localStorage.setItem('admin_logged_user', JSON.stringify(updated));
        }

        fetch(`${getApiUrl()}/api/admin/users/${updated.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                username: updated.username,
                password: updated.password,
                role: updated.role,
                hasSarahAccess: updated.hasSarahAccess
            })
        }).then(res => res.json()).then(data => {
            if (data && data.success && Array.isArray(data.users)) {
                setUsers(data.users);
            }
        }).catch(err => console.error('[AdminContext] Erreur mise à jour utilisateur serveur:', err));
    };

    const updateUserPassword = (userId: string, newPassword: string) => {
        setUsers(prev => prev.map(u => {
            if (u.id === userId) {
                const upd = { ...u, password: newPassword };
                if (currentUser && currentUser.id === userId) {
                    setCurrentUser(upd);
                    localStorage.setItem('admin_logged_user', JSON.stringify(upd));
                }
                return upd;
            }
            return u;
        }));

        fetch(`${getApiUrl()}/api/admin/users/${userId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password: newPassword })
        }).catch(err => console.error('[AdminContext] Erreur mise à jour mot de passe serveur:', err));
    };

        const toggleAi = () => {
        setIsAiEnabled(p => {
            const next = !p;
            try { localStorage.setItem('admin_ai_enabled', String(next)); } catch (e) {}
            socket.emit('update_ai_status', { isAiEnabled: next, isChatAutoAnswerEnabled });
            return next;
        });
    };
    const toggleChatAutoAnswer = () => {
        setIsChatAutoAnswerEnabled(p => {
            const next = !p;
            try { localStorage.setItem('admin_chat_auto_answer_enabled', String(next)); } catch (e) {}
            socket.emit('update_ai_status', { isAiEnabled, isChatAutoAnswerEnabled: next });
            return next;
        });
    };
    const setCustomApiKey = (k: string) => setCustomApiKeyState(k);
    const updateEmailConfig = (c: EmailConfig) => setEmailConfig(c);
    const toggleExternalEmail = () => setIsExternalEmailEnabled(p => !p);
    const updateGlobalMediumStatus = (s: GlobalMediumStatus) => {
        setGlobalMediumStatus(s);
        try { localStorage.setItem('admin_medium_global_status', s); } catch (e) {}
        socket.emit('update_medium_status', s);
    };
    const markEmailRead = (id: string) => setInbox(prev => prev.map(e => e.id === id ? { ...e, status: 'read' } : e));
    const deleteEmail = (id: string) => setInbox(prev => prev.filter(e => e.id !== id));
    
    const toggleMaintenanceMode = () => setIsMaintenanceMode(p => !p);
    const toggleLeadModal = (forcedValue?: boolean) => {
        setIsLeadModalEnabled(prev => {
            const next = typeof forcedValue === 'boolean' ? forcedValue : !prev;
            try { localStorage.setItem('astro_lead_modal_enabled', String(next)); } catch(e){}
            socket.emit('update_lead_modal_config', { isLeadModalEnabled: next });
            fetch(`${getApiUrl()}/api/admin/config/lead-modal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isLeadModalEnabled: next })
            }).catch(() => {});
            return next;
        });
    };

    return (
        <AdminContext.Provider value={{
            isAuthenticated, currentUser, login, logout,
            users, addUser, deleteUser, updateUser, updateUserPassword,
            registeredClients, refreshClients, deleteClient, updateClient, toggleClientSuspension,
            allReviews, refreshReviews, deleteReview, markReviewRead,
            isAiEnabled, toggleAi, isChatAutoAnswerEnabled, toggleChatAutoAnswer, customApiKey, setCustomApiKey,
            emailConfig, updateEmailConfig, isExternalEmailEnabled, toggleExternalEmail,
            globalMediumStatus, updateGlobalMediumStatus,
            isMaintenanceMode, toggleMaintenanceMode,
            isLeadModalEnabled, toggleLeadModal,
            promoConfig, updatePromoConfig,
            getRealDailyStats, getAdvancedStats, getTodayUniqueVisitors,
            visits, logVisit,
            sessions, createSession, addMessageToSession, getSession, markAsReadByAdmin, toggleSessionAi, deleteSession, deleteMessageFromSession, updateSessionCouponStatus,
            assignAgentToSession, toggleAgentAuthorizationForSession,
            sessionsHistory, deleteSessionHistory,
            inbox, logSubmission, markEmailRead, deleteEmail,
            getAiMemory, getAllAiMemories, updateAiMemory, deleteAiMemory,
            auditLogs, logAuditAction,
            aiLogs,
            forceRefreshData
        }}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) throw new Error("useAdmin must be used within AdminProvider");
    return context;
};